import React from 'react';
import {
  Typography,
  Button,
  Grid,
  Card,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Divider,
} from '@mui/material';
import {
  CheckCircle,
  Chat,
  Assignment,
  Notifications,
  AutoAwesome,
  ArrowForward,
  ExpandMore,
  Psychology,
  AccountTree,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { SEOHead } from '../../components/SEO';

const TaskManagementPage: React.FC = () => {
  const navigate = useNavigate();

  // FAQ data based on AI task management research
  const faqData = [
    {
      question: "How does AI task management differ from traditional project management tools?",
      answer: "Traditional task management relies on manual input and static rules. AI task management uses machine learning to automatically prioritize tasks, predict completion times, suggest optimal workflows, and adapt to changing conditions in real-time. It analyzes your work patterns to reduce manual decisions and minimize decision fatigue."
    },
    {
      question: "What makes Prismatic Forge's task management knowledge-enhanced?",
      answer: "Unlike other task managers, Prismatic Forge integrates task management with your organization's knowledge graph. This means tasks are automatically enriched with relevant context from emails, documents, and previous similar work, enabling better decision-making and reducing the time spent searching for information."
    },
    {
      question: "Can AI task management integrate with existing productivity tools?",
      answer: "Yes, most AI task managers offer seamless integration with tools like Google Calendar, Outlook, Slack, and Trello. Prismatic Forge goes further by integrating directly with your email systems and document stores to provide comprehensive context for every task."
    },
    {
      question: "How does AI prioritize tasks automatically?",
      answer: "AI task managers analyze deadlines, importance levels, your work habits, and dependencies between tasks. They use machine learning algorithms to recommend which tasks to tackle first based on factors like urgency, effort required, and potential impact on other work."
    },
    {
      question: "What security measures protect my task and project data?",
      answer: "Prismatic Forge implements enterprise-grade security including data encryption, strong authentication, and compliance with industry standards. Task data is protected with the same security measures as your organizational knowledge, ensuring sensitive project information remains secure."
    },
    {
      question: "How does AI task management improve team productivity?",
      answer: "AI task management reduces manual administrative work by 60%, improves task prioritization accuracy, and provides intelligent insights into team workflows. It helps teams focus on high-value work by automating routine decisions and surfacing relevant context automatically."
    },
    {
      question: "Can AI task management handle complex project dependencies?",
      answer: "Yes, advanced AI task managers can analyze and manage complex dependencies between tasks and projects. They can automatically adjust schedules when dependencies change, suggest optimal task sequences, and alert teams to potential bottlenecks before they become problems."
    },
    {
      question: "How quickly can I see productivity improvements with AI task management?",
      answer: "Most users see immediate benefits from intelligent task suggestions and automated prioritization. Significant productivity improvements typically occur within the first week as the AI learns your work patterns and begins providing more personalized recommendations."
    }
  ];

  const chatExamples = [
    {
      user: "I need to prepare a quarterly business review presentation",
      casey: "I'd be happy to help you with that! Let me clarify a few details:\n\n• **Goal**: Create a comprehensive quarterly business review presentation\n• **Deadline**: When do you need this completed?\n• **Deliverables**: What specific sections should be included? (metrics, achievements, challenges, next quarter plans?)\n• **Audience**: Who will be presenting to?\n\nOnce I have these details, I'll create the task and can coordinate with Sam (document processing) for research and Jordan (critique specialist) for review."
    },
    {
      user: "Set up weekly team standup meetings",
      casey: "Perfect! Let me get the details organized:\n\n• **Goal**: Establish regular weekly team standup meetings\n• **Deadline**: When should this be implemented?\n• **Deliverables**: \n  - Meeting schedule and calendar invites\n  - Agenda template\n  - Team communication about the new process\n• **Participants**: Which team members should be included?\n\nI'll also coordinate with Maya (email specialist) to handle the calendar setup and notifications."
    }
  ];

  const workflowSteps = [
    {
      agent: 'Sam',
      task: 'Research industry best practices',
      description: 'Document analysis and knowledge extraction',
      icon: <AccountTree color="primary" />,
    },
    {
      agent: 'Jordan',
      task: 'Review and critique approach',
      description: 'Quality assurance and improvement suggestions',
      icon: <Psychology color="secondary" />,
    },
    {
      agent: 'Sam',
      task: 'Refine based on feedback',
      description: 'Incorporate Jordan\'s insights and finalize',
      icon: <AccountTree color="primary" />,
    },
    {
      agent: 'You',
      task: 'Review final deliverable',
      description: 'Approve and implement the solution',
      icon: <CheckCircle color="success" />,
    }
  ];

  const features = [
    {
      icon: <Chat style={{ fontSize: 40, color: '#E94257' }} />,
      title: 'Natural Language Task Creation',
      description: 'Just tell Casey what needs to be done in plain English. No forms, no complex interfaces.',
      benefits: [
        'Chat-first interface',
        'Context preservation',
        'No learning curve',
        'Works how you think'
      ]
    },
    {
      icon: <Assignment style={{ fontSize: 40, color: '#4DC4D2' }} />,
      title: 'Intelligent Task Clarification',
      description: 'Casey asks the right questions to ensure every task has clear goals, deadlines, and deliverables.',
      benefits: [
        'Goal clarification',
        'Deadline extraction',
        'Deliverable definition',
        'Scope validation'
      ]
    },
    {
      icon: <AutoAwesome style={{ fontSize: 40, color: '#325BA8' }} />,
      title: 'Teammate-Powered Workflows',
      description: 'Multi-teammate workflows like Sam→Jordan→Sam→You for research, critique, and refinement.',
      benefits: [
        'Multi-teammate collaboration',
        'Quality assurance built-in',
        'Automated handoffs',
        'Continuous improvement'
      ]
    },
    {
      icon: <Notifications style={{ fontSize: 40, color: '#892987' }} />,
      title: 'Smart Assignment & Notifications',
      description: 'Assign tasks to team members with instant notifications and context sharing.',
      benefits: [
        'Instant notifications',
        'Full context sharing',
        'Progress tracking',
        'Team coordination'
      ]
    }
  ];

  const painPoints = [
    {
      problem: 'Complex Interfaces',
      traditional: 'Multiple screens, forms, and navigation required to create a simple task',
      solution: 'Just chat with Casey: "I need to prepare the quarterly review by Friday"',
      impact: '90% faster task creation'
    },
    {
      problem: 'Unclear Requirements',
      traditional: 'Tasks created without clear goals, leading to confusion and rework',
      solution: 'Casey clarifies goals, deadlines, and deliverables before task creation',
      impact: '60% reduction in task ambiguity'
    },
    {
      problem: 'Scope Creep',
      traditional: 'Projects expand beyond original scope with no early warning',
      solution: 'AI teammates track scope changes and alert stakeholders proactively',
      impact: '45% better scope management'
    },
    {
      problem: 'Poor Communication',
      traditional: 'Important updates lost in email chains and meeting notes',
      solution: 'Real-time notifications with full context and chat integration',
      impact: '70% improvement in team alignment'
    },
    {
      problem: 'Rigid Workflows',
      traditional: 'One-size-fits-all processes that break under real-world pressure',
      solution: 'Teammate workflows that adapt to your specific needs and context',
      impact: '80% more flexible process management'
    }
  ];

  // SEO structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Prismatic Forge Task Management",
    "description": "Context-enhanced task management powered by organizational knowledge. AI-driven prioritization and intelligent workflow automation.",
    "applicationCategory": "ProductivityApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "29",
      "priceCurrency": "USD",
      "priceSpecification": {
        "@type": "RecurringPriceSpecification",
        "billingFrequency": "monthly"
      }
    },
    "provider": {
      "@type": "Organization",
      "name": "Prismatic Forge"
    }
  };

  return (
    <div style={{
      width: '100%',
      minHeight: '100vh',
      backgroundColor: '#fafafa',
      overflowX: 'hidden',
      overflowY: 'auto',
      paddingLeft: 16,
      paddingRight: 16,
    }}>
      <SEOHead
        title="AI Task Management - Context-Enhanced Productivity"
        description="Transform task management with AI-powered prioritization and organizational knowledge context. Get intelligent task suggestions, automated workflows, and seamless team collaboration."
        keywords="AI task management, intelligent prioritization, automated workflows, productivity tools, context-enhanced tasks, AI project management, smart task automation"
        ogTitle="AI Task Management - Prismatic Forge"
        ogDescription="Experience task management enhanced by organizational knowledge. AI-powered prioritization, intelligent workflows, and context from your emails and documents."
        canonical="https://prismaticforge.ai/features/task-management"
        jsonLd={structuredData}
      />
      {/* Hero Section */}
      <div
        style={{
          background: 'linear-gradient(135deg, #0C1319 0%, #325BA8 100%)',
          color: 'white',
          paddingTop: 48,
          paddingBottom: 48,
          position: 'relative',
          overflow: 'hidden',
          marginLeft: -16,
          marginRight: -16,
        }}
      >
        <div style={{
          paddingLeft: 16,
          paddingRight: 16,
        }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                style={{
                  fontWeight: 'bold',
                  lineHeight: 1.1,
                  marginBottom: 24,
                }}
              >
                Task Management That{' '}
                <span style={{ color: '#4DC4D2' }}>
                  Actually Works
                </span>
              </Typography>
              <Typography
                variant="h5"
                style={{
                  marginBottom: 32,
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontWeight: 300,
                  lineHeight: 1.4,
                }}
              >
                Revolutionary chat-driven productivity where Casey (our project management teammate) acts as your personal task coordinator.
                No more complex interfaces—just tell Casey what needs to be done.
              </Typography>
              <Stack direction="row" spacing={2} style={{ marginBottom: 32 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/signup')}
                  style={{
                    backgroundColor: '#4DC4D2',
                    color: '#0C1319',
                    fontWeight: 'bold',
                    paddingLeft: 32,
                    paddingRight: 32,
                    paddingTop: 12,
                    paddingBottom: 12,
                  }}
                  endIcon={<ArrowForward />}
                >
                  Start Free Trial
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/chat')}
                  style={{
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    color: 'white',
                    paddingLeft: 32,
                    paddingRight: 32,
                    paddingTop: 12,
                    paddingBottom: 12,
                  }}
                >
                  Try Demo
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                color: 'white',
              }}>
                <Typography variant="h5" style={{ marginBottom: 24, fontWeight: 'bold', color: '#4DC4D2' }}>
                  What You Get:
                </Typography>
                <List style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                  <ListItem style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 8, paddingBottom: 8 }}>
                    <ListItemIcon style={{ minWidth: 40 }}>
                      <CheckCircle style={{ color: '#4DC4D2' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Chat-driven task management - just tell Casey what needs to be done"
                      primaryTypographyProps={{ fontSize: '1rem' }}
                    />
                  </ListItem>
                  <ListItem style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 8, paddingBottom: 8 }}>
                    <ListItemIcon style={{ minWidth: 40 }}>
                      <CheckCircle style={{ color: '#4DC4D2' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Context from your emails and documents enriches every task"
                      primaryTypographyProps={{ fontSize: '1rem' }}
                    />
                  </ListItem>
                  <ListItem style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 8, paddingBottom: 8 }}>
                    <ListItemIcon style={{ minWidth: 40 }}>
                      <CheckCircle style={{ color: '#4DC4D2' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="AI-powered prioritization based on your work patterns"
                      primaryTypographyProps={{ fontSize: '1rem' }}
                    />
                  </ListItem>
                  <ListItem style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 8, paddingBottom: 8 }}>
                    <ListItemIcon style={{ minWidth: 40 }}>
                      <CheckCircle style={{ color: '#4DC4D2' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Team collaboration with shared context and visibility"
                      primaryTypographyProps={{ fontSize: '1rem' }}
                    />
                  </ListItem>
                  <ListItem style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 8, paddingBottom: 8 }}>
                    <ListItemIcon style={{ minWidth: 40 }}>
                      <CheckCircle style={{ color: '#4DC4D2' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="60% reduction in administrative overhead"
                      primaryTypographyProps={{ fontSize: '1rem' }}
                    />
                  </ListItem>
                </List>
              </div>
            </Grid>
          </Grid>
        </div>
      </div>

      {/* Core Features Section */}
      <div style={{ paddingTop: 80, paddingBottom: 80 }}>
        <Typography
          variant="h3"
          component="h2"
          textAlign="center"
          gutterBottom
          style={{ marginBottom: 64, fontWeight: 'bold', color: '#325BA8' }}
        >
          Revolutionary Features That Change Everything
        </Typography>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card
                style={{
                  height: '100%',
                  padding: 24,
                  border: '1px solid rgba(233, 66, 87, 0.2)',
                  transition: 'all 0.3s ease',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 16 }}>
                  {feature.icon}
                  <div style={{ marginLeft: 16, flex: 1 }}>
                    <Typography variant="h5" gutterBottom style={{ fontWeight: 'bold' }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body1" style={{ color: 'rgba(0,0,0,0.6)', marginBottom: 24 }}>
                      {feature.description}
                    </Typography>
                    <List dense>
                      {feature.benefits.map((benefit, i) => (
                        <ListItem key={i} style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 4, paddingBottom: 4 }}>
                          <ListItemIcon style={{ minWidth: 32 }}>
                            <CheckCircle style={{ fontSize: 16, color: '#4DC4D2' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={benefit}
                            primaryTypographyProps={{ fontSize: '0.875rem' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </div>
                </div>
              </Card>
            </Grid>
          ))}
        </Grid>
      </div>

      {/* Teammate Workflow Section */}
      <div style={{
        backgroundColor: '#fff',
        paddingTop: 64,
        paddingBottom: 64,
        marginLeft: -16,
        marginRight: -16,
        paddingLeft: 16,
        paddingRight: 16,
      }}>
        <div>
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            style={{ marginBottom: 48, fontWeight: 'bold', color: '#325BA8' }}
          >
            Teammate-Powered Workflows in Action
          </Typography>
          <Typography
            variant="h6"
            textAlign="center"
            style={{ marginBottom: 64, color: 'rgba(0,0,0,0.6)', maxWidth: 600, marginLeft: 'auto', marginRight: 'auto' }}
          >
            Watch how multiple AI teammates collaborate to deliver better results than any single teammate could achieve alone.
          </Typography>

          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h5" gutterBottom style={{ fontWeight: 'bold', marginBottom: 24 }}>
                Example: Research & Analysis Workflow
              </Typography>
              <div>
                {workflowSteps.map((step, index) => (
                  <div key={index} style={{ display: 'flex', gap: 16 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40 }}>
                        {step.icon}
                      </div>
                      {index < workflowSteps.length - 1 && (
                        <div style={{ width: 2, flexGrow: 1, backgroundColor: '#e0e0e0', minHeight: 24 }} />
                      )}
                    </div>
                    <div style={{ paddingBottom: index < workflowSteps.length - 1 ? 24 : 0 }}>
                      <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                        {step.agent}: {step.task}
                      </Typography>
                      <Typography variant="body2" style={{ color: 'rgba(0,0,0,0.6)' }}>
                        {step.description}
                      </Typography>
                    </div>
                  </div>
                ))}
              </div>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card style={{ padding: 32, backgroundColor: 'rgba(76, 196, 210, 0.05)', border: '1px solid rgba(76, 196, 210, 0.2)' }}>
                <Typography variant="h5" gutterBottom style={{ fontWeight: 'bold', color: '#4DC4D2' }}>
                  The Result
                </Typography>
                <List>
                  {[
                    'Higher quality output through peer review',
                    'Consistent improvement via Jordan\'s critique',
                    'Reduced manual oversight and management',
                    'Automated quality assurance built-in',
                    'Faster iteration cycles'
                  ].map((benefit, index) => (
                    <ListItem key={index} style={{ paddingLeft: 0 }}>
                      <ListItemIcon>
                        <CheckCircle style={{ color: '#4DC4D2' }} />
                      </ListItemIcon>
                      <ListItemText primary={benefit} />
                    </ListItem>
                  ))}
                </List>
              </Card>
            </Grid>
          </Grid>
        </div>
      </div>

      {/* Problem-Solution Section */}
      <div style={{ paddingTop: 80, paddingBottom: 80 }}>
        <Typography
          variant="h3"
          component="h2"
          textAlign="center"
          gutterBottom
          style={{ marginBottom: 48, fontWeight: 'bold', color: '#325BA8' }}
        >
          Solving Real Problems with PM Software
        </Typography>
        <Grid container spacing={3}>
          {painPoints.map((point, index) => (
            <Grid item xs={12} key={index}>
              <Accordion
                style={{
                  border: '1px solid rgba(233, 66, 87, 0.2)',
                  borderRadius: 8,
                  overflow: 'hidden',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  style={{
                    backgroundColor: 'rgba(233, 66, 87, 0.05)',
                  }}
                >
                  <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                    {point.problem}
                    <Chip
                      label={point.impact}
                      size="small"
                      style={{
                        marginLeft: 16,
                        backgroundColor: '#4DC4D2',
                        color: 'white',
                        fontWeight: 'bold',
                      }}
                    />
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" style={{ fontWeight: 'bold', color: '#E94257', marginBottom: 8 }}>
                        Traditional Problem:
                      </Typography>
                      <Typography variant="body2" style={{ color: 'rgba(0,0,0,0.6)', marginBottom: 16 }}>
                        {point.traditional}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" style={{ fontWeight: 'bold', color: '#4DC4D2', marginBottom: 8 }}>
                        Our Solution:
                      </Typography>
                      <Typography variant="body2" style={{ color: 'rgba(0,0,0,0.6)' }}>
                        {point.solution}
                      </Typography>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
          ))}
        </Grid>
      </div>

      {/* Interactive Chat Examples */}
      <div style={{
        backgroundColor: '#fff',
        paddingTop: 64,
        paddingBottom: 64,
        marginLeft: -16,
        marginRight: -16,
        paddingLeft: 16,
        paddingRight: 16,
      }}>
        <div>
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            style={{ marginBottom: 48, fontWeight: 'bold', color: '#325BA8' }}
          >
            See Casey in Action
          </Typography>
          <Grid container spacing={4}>
            {chatExamples.map((example, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Paper
                  elevation={4}
                  style={{
                    padding: 32,
                    height: '100%',
                    border: '1px solid rgba(233, 66, 87, 0.2)',
                  }}
                >
                  <Typography variant="h6" gutterBottom style={{ color: '#E94257', fontWeight: 'bold' }}>
                    Example {index + 1}
                  </Typography>
                  <div style={{ marginBottom: 24 }}>
                    <Typography variant="body2" style={{ color: 'rgba(0,0,0,0.6)', marginBottom: 8 }}>
                      You:
                    </Typography>
                    <Typography variant="body1" style={{ fontStyle: 'italic', marginBottom: 16, padding: 16, backgroundColor: 'rgba(0,0,0,0.02)', borderRadius: 4 }}>
                      "{example.user}"
                    </Typography>
                  </div>
                  <div>
                    <Typography variant="body2" style={{ color: '#E94257', marginBottom: 8, fontWeight: 'bold' }}>
                      Casey:
                    </Typography>
                    <Typography variant="body2" style={{ lineHeight: 1.6, whiteSpace: 'pre-line', padding: 16, backgroundColor: 'rgba(233, 66, 87, 0.05)', borderRadius: 4 }}>
                      {example.casey}
                    </Typography>
                  </div>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </div>
      </div>

      {/* CTA Section */}
      <div style={{ paddingTop: 80, paddingBottom: 80, textAlign: 'center', maxWidth: 1200, marginLeft: 'auto', marginRight: 'auto' }}>
        <Typography
          variant="h3"
          component="h2"
          gutterBottom
          style={{ fontWeight: 'bold', marginBottom: 24 }}
        >
          Ready to Transform Your Task Management?
        </Typography>
        <Typography
          variant="h6"
          style={{ marginBottom: 32, color: 'rgba(0,0,0,0.6)', fontWeight: 300, maxWidth: 600, marginLeft: 'auto', marginRight: 'auto' }}
        >
          Join the productivity revolution. Experience task management that actually works with your brain, not against it.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/signup')}
            style={{
              backgroundColor: '#E94257',
              color: 'white',
              fontWeight: 'bold',
              paddingLeft: 48,
              paddingRight: 48,
              paddingTop: 16,
              paddingBottom: 16,
              fontSize: '1.1rem',
            }}
            endIcon={<ArrowForward />}
          >
            Start Your Free Trial
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate('/login')}
            style={{
              borderColor: '#E94257',
              color: '#E94257',
              fontWeight: 'bold',
              paddingLeft: 48,
              paddingRight: 48,
              paddingTop: 16,
              paddingBottom: 16,
              fontSize: '1.1rem',
            }}
          >
            Login
          </Button>
        </Stack>
        <Typography variant="body2" style={{ marginTop: 24, color: 'rgba(0,0,0,0.6)' }}>
          30-day free trial • No credit card required • Cancel anytime
        </Typography>
      </div>

      {/* FAQ Section */}
      <div style={{ paddingTop: 80, paddingBottom: 80 }}>
        <Typography
          variant="h3"
          component="h2"
          gutterBottom
          style={{ fontWeight: 'bold', marginBottom: 48, textAlign: 'center' }}
        >
          Frequently Asked Questions
        </Typography>
        <Typography
          variant="h6"
          style={{ marginBottom: 48, color: 'rgba(0,0,0,0.6)', textAlign: 'center', maxWidth: 600, marginLeft: 'auto', marginRight: 'auto' }}
        >
          Everything you need to know about AI-powered task management
        </Typography>

        <Grid container spacing={3}>
          {faqData.map((faq, index) => (
            <Grid item xs={12} key={index}>
              <Accordion
                style={{
                  backgroundColor: '#fff',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                >
                  <Typography variant="h6" style={{ fontWeight: 500 }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails style={{ paddingTop: 0, paddingBottom: 24 }}>
                  <Typography variant="body1" style={{ color: 'rgba(0,0,0,0.6)', lineHeight: 1.7 }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Grid>
          ))}
        </Grid>
      </div>

      {/* Footer */}
      <div style={{ marginLeft: -16, marginRight: -16 }}>
        <Divider />
      </div>
      <div style={{ paddingTop: 32, paddingBottom: 32 }}>
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap',
        }}>
          <Typography variant="body2" style={{ color: 'rgba(0,0,0,0.6)' }}>
            © 2025 Prismatic Forge. All rights reserved.
          </Typography>
          <div style={{
            display: 'flex',
            gap: 24,
            alignItems: 'center',
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}>
            <Typography
              component="a"
              href="/terms"
              variant="body2"
              style={{
                color: 'rgba(0,0,0,0.6)',
                textDecoration: 'none',
              }}
            >
              Terms of Service
            </Typography>
            <Typography
              component="a"
              href="/privacy/"
              variant="body2"
              style={{
                color: 'rgba(0,0,0,0.6)',
                textDecoration: 'none',
              }}
            >
              Privacy Policy
            </Typography>
            <Typography
              component="a"
              href="/docs"
              variant="body2"
              style={{
                color: 'rgba(0,0,0,0.6)',
                textDecoration: 'none',
              }}
            >
              Documentation
            </Typography>
            <Typography
              component="a"
              href="mailto:support@prismaticforge.ai"
              variant="body2"
              style={{
                color: 'rgba(0,0,0,0.6)',
                textDecoration: 'none',
              }}
            >
              Contact Support
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskManagementPage;
